import React, { useCallback, useEffect, useState } from "react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import {
  Activity, AlertTriangle, BarChart3, CheckCircle2, Clock3, RefreshCw,
  Shield, Sparkles, Users,
} from "lucide-react";
import {
  Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from "recharts";

const EVENT_NAMES = [
  "extension.installed", "extension.upgraded", "extension.active", "extension.feature_used",
  "extension.error", "extension.permission", "extension.cookie_sync", "extension.earnings_capture",
  "extension.post_queue", "extension.sync", "platform.connection", "platform.api_request",
  "platform.rate_limit", "platform.post_published", "platform.insights_synced", "platform.sync",
  "ai.generation", "crm.login", "crm.feature_used", "crm.subscription_status", "crm.promo_generation",
];
const PROVIDERS = ["gemini", "xai", "openai", "anthropic", "other"];
const FAILURE_LABELS = {
  billing_limit: "Billing or spending limit",
  authentication: "Authentication",
  rate_limit: "Rate limit",
  timeout: "Timeout",
  validation: "Validation",
  network: "Network",
  permission: "Permission",
  server: "Server",
  unknown: "Other",
};
const PROVIDER_LABELS = { gemini: "Gemini", xai: "Grok (xAI)", openai: "OpenAI", anthropic: "Anthropic" };

const number = (value) => Number(value || 0).toLocaleString();
const rate = (requests, failures) => requests ? `${(((requests - failures) / requests) * 100).toFixed(1)}%` : "No requests";
const providerLabel = (provider) => PROVIDER_LABELS[provider] || provider || "Not configured";

function Metric({ label, value, detail }) {
  return (
    <div className="min-w-0 border border-zinc-800 bg-zinc-950 px-4 py-3">
      <div className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="mt-1 break-words text-xl font-semibold text-white">{value}</div>
      {detail && <div className="mt-1 text-xs text-zinc-500">{detail}</div>}
    </div>
  );
}

function Section({ title, icon: Icon, children, testId }) {
  return (
    <section className="border border-zinc-800 bg-zinc-950/70 p-4 sm:p-5" aria-label={title} data-testid={testId}>
      <div className="mb-4 flex items-center gap-2 border-b border-zinc-800 pb-3">
        <Icon size={16} className="text-emerald-400" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-100">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function Empty({ children }) {
  return <p className="py-5 text-sm text-zinc-500">{children}</p>;
}

function ConnectionStatus({ connected, status }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs ${connected ? "text-emerald-400" : "text-zinc-400"}`}>
      {connected ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
      {status || (connected ? "Connected" : "Not connected")}
    </span>
  );
}

export default function AnalyticsDashboard({ embedded = false }) {
  const [days, setDays] = useState("30");
  const [granularity, setGranularity] = useState("daily");
  const [version, setVersion] = useState("");
  const [provider, setProvider] = useState("");
  const [platform, setPlatform] = useState("");
  const [eventType, setEventType] = useState("");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const params = new URLSearchParams({ days, granularity });
    if (version) params.set("version", version);
    if (provider) params.set("provider", provider);
    if (platform) params.set("platform", platform);
    if (eventType) params.set("event_type", eventType);
    try {
      const response = await api.get(`/api/analytics/unified/summary?${params.toString()}`);
      setSummary(response.data);
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Analytics could not be loaded. Try again.");
    } finally {
      setLoading(false);
    }
  }, [days, granularity, version, provider, platform, eventType]);

  useEffect(() => { load(); }, [load]);

  const extension = summary?.extension || {};
  const platforms = summary?.platforms || {};
  const ai = summary?.ai || {};
  const crm = summary?.crm || {};
  const x = platforms.x || {};
  const meta = platforms.meta || {};
  const legacy = summary?.legacy_metrics || {};
  const xHistory = legacy.x || {};
  const metaHistory = legacy.instagram || legacy.meta || {};
  const versions = extension.versions || [];
  const trends = summary?.trends || [];
  const eventCounts = summary?.events?.by_name || {};
  const providerRows = ai.by_provider || [];
  const configured = new Set(ai.configured_providers || []);
  const labels = summary?.labels?.events || {};
  const metricLabels = summary?.labels?.metrics || {};
  const filterBar = (
    <div className="grid grid-cols-2 gap-3 border border-zinc-800 bg-zinc-950 p-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="analytics-filters">
      <label className="text-xs text-zinc-500">Date range
        <select data-testid="analytics-days" value={days} onChange={(event) => setDays(event.target.value)} className="mt-1 block w-full border border-zinc-700 bg-zinc-900 px-2 py-2 text-sm text-white">
          <option value="7">Last 7 days</option><option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option><option value="365">Last 12 months</option>
        </select>
      </label>
      <label className="text-xs text-zinc-500">Aggregation
        <select data-testid="analytics-granularity" value={granularity} onChange={(event) => setGranularity(event.target.value)} className="mt-1 block w-full border border-zinc-700 bg-zinc-900 px-2 py-2 text-sm text-white">
          <option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option>
        </select>
      </label>
      <label className="text-xs text-zinc-500">Extension version
        <select data-testid="analytics-version" value={version} onChange={(event) => setVersion(event.target.value)} className="mt-1 block w-full border border-zinc-700 bg-zinc-900 px-2 py-2 text-sm text-white">
          <option value="">All versions</option>{versions.map((item) => <option key={item.version} value={item.version}>{item.version}</option>)}
        </select>
      </label>
      <label className="text-xs text-zinc-500">AI provider
        <select data-testid="analytics-provider" value={provider} onChange={(event) => setProvider(event.target.value)} className="mt-1 block w-full border border-zinc-700 bg-zinc-900 px-2 py-2 text-sm text-white">
          <option value="">All providers</option>{PROVIDERS.map((item) => <option key={item} value={item}>{providerLabel(item)}</option>)}
        </select>
      </label>
      <label className="text-xs text-zinc-500">Platform
        <select data-testid="analytics-platform" value={platform} onChange={(event) => setPlatform(event.target.value)} className="mt-1 block w-full border border-zinc-700 bg-zinc-900 px-2 py-2 text-sm text-white">
          <option value="">X and Meta</option><option value="x">X</option><option value="meta">Meta</option>
        </select>
      </label>
      <label className="text-xs text-zinc-500">Event type
        <select data-testid="analytics-event-type" value={eventType} onChange={(event) => setEventType(event.target.value)} className="mt-1 block w-full border border-zinc-700 bg-zinc-900 px-2 py-2 text-sm text-white">
          <option value="">All event types</option>{EVENT_NAMES.map((item) => <option key={item} value={item}>{labels[item] || item}</option>)}
        </select>
      </label>
    </div>
  );

  const body = (
    <div className="space-y-5" data-testid="analytics-dashboard">
      {filterBar}
      {error && <div role="alert" className="flex items-center justify-between gap-3 border border-red-900/70 bg-red-950/30 p-3 text-sm text-red-200">
        <span>{error}</span><button type="button" className="btn-ghost inline-flex items-center gap-2" onClick={load}><RefreshCw size={14} />Retry</button>
      </div>}
      {loading && !summary ? <p className="py-10 text-center text-sm text-zinc-500">Loading analytics…</p> : summary && (
        <>
          <div className="flex items-center justify-between gap-3 text-xs text-zinc-500">
            <span>{summary.events?.total ? `${number(summary.events.total)} events in selected range` : "No product events in selected range"}</span>
            {summary.labels?.retention_days && <span>Retention target: {number(summary.labels.retention_days)} days · event records are not auto-deleted</span>}
          </div>
          <Section title="Extension Health" icon={Activity} testId="analytics-extension">
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <Metric label="Installs reported" value={number(extension.installs)} />
              <Metric label="Active installations" value={number(extension.active_users)} detail="Opt-in telemetry only" />
              <Metric label="Version 0.7.0 observed" value={number(versions.find((item) => item.version === "0.7.0")?.active_users)} detail={`${number(versions.find((item) => item.version === "0.7.0")?.observed_connections)} paired connections seen`} />
              <Metric label="Errors / permission issues" value={`${number(extension.errors)} / ${number(extension.permission_issues)}`} detail={`${number(extension.sync_failures)} sync failures`} />
            </div>
            <div className="mt-5 grid gap-5 lg:grid-cols-2">
              <div><h3 className="mb-2 text-xs uppercase tracking-wider text-zinc-500">Adoption by version</h3>
                {versions.length ? <div className="h-56"><ResponsiveContainer width="100%" height="100%"><BarChart data={versions}><CartesianGrid stroke="#27272a" /><XAxis dataKey="version" stroke="#8c8c96" /><YAxis stroke="#8c8c96" /><Tooltip /><Legend /><Bar dataKey="installs" name="Installs" fill="#34d399" /><Bar dataKey="active_users" name="Active" fill="#38bdf8" /></BarChart></ResponsiveContainer></div> : <Empty>No version activity recorded yet.</Empty>}
              </div>
              <div><h3 className="mb-2 text-xs uppercase tracking-wider text-zinc-500">Feature usage</h3>
                {Object.keys(extension.feature_usage || {}).length ? <ul className="divide-y divide-zinc-800">{Object.entries(extension.feature_usage).sort((a, b) => b[1] - a[1]).map(([feature, count]) => <li key={feature} className="flex justify-between gap-4 py-2 text-sm"><span className="break-all text-zinc-300">{feature}</span><strong className="text-white">{number(count)}</strong></li>)}</ul> : <Empty>Feature use will appear after users opt in to anonymous extension diagnostics.</Empty>}
              </div>
            </div>
            {versions.length > 0 && <p className="mt-3 text-xs text-zinc-500">Reported installs and active users require opt-in. Paired connection counts are observed separately and do not represent total installs.</p>}
          </Section>

          <div className="grid gap-5 xl:grid-cols-2">
            {platform !== "meta" && <Section title="X API Health" icon={Activity} testId="analytics-x">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3"><ConnectionStatus connected={Boolean(x.connected)} status={x.connection_status} /><span className="text-xs text-zinc-500">Last successful sync: {x.last_successful_sync ? new Date(x.last_successful_sync).toLocaleString() : "Not yet reported"}</span></div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Metric label="API success rate" value={rate(x.requests, x.failures)} />
                <Metric label="Requests / failures" value={`${number(x.requests)} / ${number(x.failures)}`} />
                <Metric label="Rate limits" value={number(x.rate_limits)} />
                <Metric label="Posts published" value={number(x.posts_published)} />
              </div>
              {xHistory.days ? <p className="mt-4 text-xs text-zinc-400">Historical X metrics: {number(xHistory.impressions)} {metricLabels.impressions || "impressions"} · {number(xHistory.engagements)} {metricLabels.engagements || "engagements"} · {number(xHistory.likes)} {metricLabels.likes || "likes"}.</p> : <Empty>{x.connected ? "No engagement metrics have been recorded in this date range." : "Connect X in the CRM to report account and API health."}</Empty>}
            </Section>}
            {platform !== "x" && <Section title="Meta / Instagram API Health" icon={Shield} testId="analytics-meta">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3"><ConnectionStatus connected={Boolean(meta.connected)} status={meta.state} /><span className="text-xs text-zinc-500">Token: {meta.days_left == null ? "Status unavailable" : meta.days_left <= 7 ? `Expires in ${number(meta.days_left)} days` : `Valid for ${number(meta.days_left)} days`}</span></div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Metric label="API success rate" value={rate(meta.requests, meta.failures)} />
                <Metric label="Requests / failures" value={`${number(meta.requests)} / ${number(meta.failures)}`} />
                <Metric label="Rate limits" value={number(meta.rate_limits)} />
                <Metric label="Published content" value={number(meta.published_content)} />
              </div>
              {metaHistory.days ? <p className="mt-4 text-xs text-zinc-400">Historical Instagram metrics: {number(metaHistory.impressions)} {metricLabels.impressions || "impressions"} · {number(metaHistory.engagements)} {metricLabels.engagements || "engagements"} · {number(metaHistory.likes)} {metricLabels.likes || "likes"}.</p> : <Empty>{meta.connected ? "No imported or synced Instagram metrics in this range." : "Instagram / Meta is not connected. Connect it from CRM Social settings."}</Empty>}
              {meta.connected && !metaHistory.days && <p className="text-xs text-zinc-500">Meta insights are not available from the current connected API scopes.</p>}
            </Section>}
          </div>

          <Section title="AI Generation" icon={Sparkles} testId="analytics-ai">
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <Metric label="Generation requests" value={number(ai.requests)} />
              <Metric label="Success rate" value={ai.success_rate == null ? "No requests" : `${ai.success_rate}%`} detail={`${number(ai.successes)} successful · ${number(ai.failures)} failed`} />
              <Metric label="Average latency" value={ai.average_latency_ms == null ? "Not available" : `${number(ai.average_latency_ms)} ms`} />
              <Metric label="Free-member promo requests" value={number(ai.free_member_promo_requests)} />
            </div>
            <div className="mt-5 grid gap-5 lg:grid-cols-2">
              <div><h3 className="mb-2 text-xs uppercase tracking-wider text-zinc-500">Provider breakdown</h3>
                {providerRows.length ? <ul className="divide-y divide-zinc-800">{providerRows.map((item) => <li key={item.provider || "unknown"} className="flex justify-between gap-4 py-2 text-sm"><span className="text-zinc-300">{providerLabel(item.provider)}</span><span className="text-zinc-400">{number(item.successes)} success · {number(item.failures)} failed</span></li>)}</ul> : <Empty>{configured.size ? "No AI generation requests in this range." : "Gemini and xAI/Grok are not configured."}</Empty>}
              </div>
              <div><h3 className="mb-2 text-xs uppercase tracking-wider text-zinc-500">Failure reasons</h3>
                {Object.keys(ai.failures_by_type || {}).length ? <ul className="divide-y divide-zinc-800">{Object.entries(ai.failures_by_type).map(([kind, count]) => <li key={kind} className="flex justify-between gap-4 py-2 text-sm"><span className="text-zinc-300">{FAILURE_LABELS[kind] || kind}</span><strong className="text-white">{number(count)}</strong></li>)}</ul> : <Empty>No generation failures recorded.</Empty>}
              </div>
            </div>
          </Section>

          <div className="grid gap-5 xl:grid-cols-2">
            <Section title="CRM Usage" icon={Users} testId="analytics-crm">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Metric label="Active admin users" value={number(crm.active_users)} />
                <Metric label="Logins reported" value={number(crm.logins)} detail="Login event history begins with the new event service" />
                <Metric label="API activity" value={number(crm.api_activity)} />
                <Metric label="Member tiers" value={number(Object.values(crm.members_by_tier || {}).reduce((sum, count) => sum + count, 0))} />
              </div>
              <div className="mt-5 grid gap-5 lg:grid-cols-2">
                <div><h3 className="mb-2 text-xs uppercase tracking-wider text-zinc-500">Feature adoption</h3>
                  {Object.keys(crm.feature_usage || {}).length ? <ul className="divide-y divide-zinc-800">{Object.entries(crm.feature_usage).map(([feature, count]) => <li key={feature} className="flex justify-between gap-4 py-2 text-sm"><span className="text-zinc-300">{feature}</span><strong className="text-white">{number(count)}</strong></li>)}</ul> : <Empty>CRM feature events will appear as the new instrumentation records usage.</Empty>}
                </div>
                <div><h3 className="mb-2 text-xs uppercase tracking-wider text-zinc-500">Subscription / free-member distribution</h3>
                  {Object.keys(crm.members_by_tier || {}).length ? <ul className="divide-y divide-zinc-800">{Object.entries(crm.members_by_tier).map(([tier, count]) => <li key={tier} className="flex justify-between gap-4 py-2 text-sm"><span className="capitalize text-zinc-300">{tier.replaceAll("_", " ")}</span><span className="text-zinc-400">{number(count)} total · {number(crm.active_members_by_tier?.[tier])} active</span></li>)}</ul> : <Empty>No member tier data available.</Empty>}
                </div>
              </div>
            </Section>
            <Section title="Activity Trends" icon={BarChart3} testId="analytics-trends">
              {trends.length ? <div className="h-64"><ResponsiveContainer width="100%" height="100%"><LineChart data={trends}><CartesianGrid stroke="#27272a" /><XAxis dataKey="period" stroke="#8c8c96" /><YAxis stroke="#8c8c96" /><Tooltip /><Legend /><Line type="monotone" dataKey="successes" stroke="#34d399" /><Line type="monotone" dataKey="failures" stroke="#fb7185" /></LineChart></ResponsiveContainer></div> : <Empty>No activity trend data in this range.</Empty>}
              <div className="mt-3 flex items-center gap-2 text-xs text-zinc-500"><Clock3 size={13} /> Aggregates only · no raw event records shown</div>
            </Section>
          </div>
          {loading && <p className="text-xs text-zinc-500">Updating report…</p>}
          {summary.source_status?.events === "empty" && <p className="border border-zinc-800 px-3 py-2 text-xs text-zinc-500">No new analytics events yet. Historical aggregates remain available where supported.</p>}
        </>
      )}
    </div>
  );

  if (embedded) return body;
  return (
    <AppLayout title="Analytics" overline="PRODUCT, PLATFORM & EXTENSION HEALTH" actions={
      <button type="button" onClick={load} disabled={loading} className="btn-ghost inline-flex items-center gap-2" data-testid="analytics-refresh" aria-label="Refresh analytics">
        <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
      </button>
    }>{body}</AppLayout>
  );
}